import { useState } from 'react';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import '../sass/LoginModal.scss';

export default function LoginModal({ onClose, onSuccess }) {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      onSuccess?.();
      onClose?.();
    } catch (e) {
      setError('No se pudo iniciar con Google. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Ingresa tu correo arriba y luego haz clic en "Olvidé mi contraseña".');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setInfo(`Te enviamos un email a ${email} para restablecer tu contraseña.`);
    } catch (e) {
      const msgs = {
        'auth/user-not-found': 'No existe una cuenta con ese correo.',
        'auth/invalid-email':  'El correo no es válido.',
      };
      setError(msgs[e.code] || 'No se pudo enviar el email. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await signInWithEmailAndPassword(auth, email, password);
        onSuccess?.();
        onClose?.();
      } else {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        if (name.trim()) await updateProfile(cred.user, { displayName: name.trim() });
        await sendEmailVerification(cred.user);
        onSuccess?.();
        onClose?.();
      }
    } catch (e) {
      const msgs = {
        'auth/user-not-found':     'No existe una cuenta con ese correo.',
        'auth/wrong-password':     'Contraseña incorrecta.',
        'auth/email-already-in-use': 'Ya existe una cuenta con ese correo.',
        'auth/weak-password':      'La contraseña debe tener al menos 6 caracteres.',
        'auth/invalid-email':      'El correo no es válido.',
        'auth/invalid-credential': 'Correo o contraseña incorrectos.',
      };
      setError(msgs[e.code] || 'Ocurrió un error. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-overlay" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="login-modal">
        <button className="login-close" onClick={onClose}>✕</button>

        <div className="login-header">
          <div className="login-icon">🔐</div>
          <h3>{mode === 'login' ? 'Inicia sesión' : 'Crea tu cuenta'}</h3>
          <p>{mode === 'login'
            ? 'Para publicar tu auto necesitas una cuenta.'
            : 'Es gratis y solo toma un momento.'}
          </p>
        </div>

        <button className="btn-google" onClick={handleGoogle} disabled={loading}>
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
          Continuar con Google
        </button>

        <div className="login-divider"><span>o</span></div>

        <form onSubmit={handleEmail}>
          {mode === 'register' && (
            <input
              type="text"
              placeholder="Tu nombre"
              value={name}
              onChange={e => setName(e.target.value)}
              autoComplete="name"
            />
          )}
          <input
            type="email"
            placeholder="Correo electrónico"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <input
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />

          {mode === 'login' && (
            <button
              type="button"
              className="btn-forgot"
              onClick={handleForgotPassword}
              disabled={loading}
            >
              ¿Olvidaste tu contraseña?
            </button>
          )}

          {error && <div className="login-error">{error}</div>}
          {info  && <div className="login-info">{info}</div>}

          <button type="submit" className="btn-email-submit" disabled={loading}>
            {loading ? 'Cargando...' : mode === 'login' ? 'Ingresar' : 'Crear cuenta'}
          </button>
        </form>

        <p className="login-toggle">
          {mode === 'login'
            ? <>¿No tienes cuenta? <button onClick={() => { setMode('register'); setError(''); }}>Regístrate gratis</button></>
            : <>¿Ya tienes cuenta? <button onClick={() => { setMode('login'); setError(''); }}>Ingresa</button></>
          }
        </p>
      </div>
    </div>
  );
}
